import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { supabase } from '../lib/supabaseClient';

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isValidSession, setIsValidSession] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  // Check if user has valid password reset session
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Session check error:', error);
          setMessage({
            type: 'error',
            text: 'Невалидна или изтекла сесия за смяна на парола'
          });
          return;
        }

        if (!session) {
          setMessage({
            type: 'error',
            text: 'Невалидна или изтекла сесия за смяна на парола'
          });
          return;
        }

        setIsValidSession(true);
      } catch (error) {
        console.error('Session check error:', error);
        setMessage({
          type: 'error',
          text: 'Възникна грешка при проверка на сесията'
        });
      } finally {
        setIsCheckingSession(false);
      }
    };

    checkSession();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setMessage({
        type: 'error',
        text: 'Паролите не съвпадат'
      });
      return;
    }

    if (password.length < 6) {
      setMessage({
        type: 'error',
        text: 'Паролата трябва да бъде поне 6 символа'
      });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        console.error('Password update error:', error);
        setMessage({
          type: 'error',
          text: 'Грешка при смяна на паролата'
        });
        return;
      }

      setMessage({
        type: 'success',
        text: 'Паролата е сменена успешно! Ще бъдете пренасочени към страницата за влизане.'
      });

      // Redirect to login page after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (error) {
      console.error('Password update error:', error);
      setMessage({
        type: 'error',
        text: 'Възникна неочаквана грешка при смяна на паролата'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isCheckingSession) {
    return (
      <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: '#28A0F0' }} />
      </Box>
    );
  }

  if (!isValidSession) {
    return (
      <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Container maxWidth="sm">
            <Card sx={{
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 4,
              overflow: 'hidden',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
            }}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ color: '#dc3545', mb: 2 }}>
                  Невалидна сесия
                </Typography>
                <Typography variant="body1" sx={{ color: '#a0a0a0', mb: 3 }}>
                  Линкът за смяна на парола е невалиден или е изтекъл. Моля, заявете нова смяна на парола.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/login')}
                  sx={{
                    background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                  }}
                >
                  Към страницата за влизане
                </Button>
              </CardContent>
            </Card>
          </Container>
        </Box>
        <Footer />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />
      
      {/* Hero Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
        borderBottom: '1px solid rgba(40, 160, 240, 0.2)',
        py: 6,
        flex: 1,
        display: 'flex',
        alignItems: 'center'
      }}>
        <Container maxWidth="sm" sx={{ width: isMobile ? '90%' : '100%' }}>
          <Card sx={{
            background: 'rgba(255, 255, 255, 0.02)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
          }}>
            <CardContent sx={{ p: 4 }}>
              {/* Header */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Box sx={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
                  mb: 3,
                  boxShadow: '0 8px 20px rgba(40, 160, 240, 0.3)'
                }}>
                  <LockIcon sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                
                <Typography 
                  variant="h4" 
                  sx={{ 
                    color: '#ffffff', 
                    fontWeight: 700, 
                    mb: 2,
                    background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Смяна на парола
                </Typography>
                
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: '#a0a0a0', 
                    lineHeight: 1.6,
                    maxWidth: 400,
                    mx: 'auto'
                  }}
                >
                  Въведете новата си парола по-долу.
                </Typography>
              </Box>

              {/* Form */}
              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  label={t('auth.resetPassword.newPassword')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      color: '#ffffff',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(40, 160, 240, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#28A0F0',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#a0a0a0',
                      '&.Mui-focused': {
                        color: '#28A0F0',
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: <LockIcon sx={{ color: '#a0a0a0', mr: 1 }} />,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: '#a0a0a0' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  type={showConfirmPassword ? 'text' : 'password'}
                  label={t('auth.resetPassword.confirmNewPassword')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      color: '#ffffff',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(40, 160, 240, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#28A0F0',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#a0a0a0',
                      '&.Mui-focused': {
                        color: '#28A0F0',
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: <LockIcon sx={{ color: '#a0a0a0', mr: 1 }} />,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          sx={{ color: '#a0a0a0' }}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {message && (
                  <Alert 
                    severity={message.type} 
                    sx={{ 
                      mb: 3,
                      backgroundColor: message.type === 'success' ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)',
                      border: `1px solid ${message.type === 'success' ? 'rgba(40, 167, 69, 0.3)' : 'rgba(220, 53, 69, 0.3)'}`,
                      color: message.type === 'success' ? '#28a745' : '#dc3545',
                    }}
                    icon={message.type === 'success' ? <CheckCircleIcon /> : undefined}
                  >
                    {message.text}
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isLoading || !password || !confirmPassword}
                  sx={{
                    background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    boxShadow: '0 8px 20px rgba(40, 160, 240, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1d4ed8 0%, #be185d 100%)',
                      boxShadow: '0 12px 25px rgba(40, 160, 240, 0.4)',
                    },
                    '&:disabled': {
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'rgba(255, 255, 255, 0.3)',
                    },
                  }}
                  startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <LockIcon />}
                >
                  {isLoading ? 'Смяна на парола...' : 'Смени паролата'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
      
      <Footer />
    </Box>
  );
};

export default ResetPasswordPage;
